import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

export const env = createEnv({
  clientPrefix: "PUBLIC_",
  client: {
    // Core API URL
    PUBLIC_PLATFORM_URL: z.url(),

    // LiveKit (video meet) server URL (ws/wss)
    PUBLIC_LIVEKIT_URL: z.url().optional(),

    // File Upload Limits (in MB)
    PUBLIC_MAX_FILE_SIZE: z.coerce.number(),
    PUBLIC_MAX_TOTAL_FILES_SIZE: z.coerce.number(),

    // Clerk Authentication
    PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
    PUBLIC_CLERK_SIGN_IN_URL: z.url(),
    // Clerk satellite mode: run a production Clerk instance on a non-primary
    // domain (e.g. a preview/tunnel URL). When true, sign-in happens on the
    // primary (PUBLIC_CLERK_SIGN_IN_URL) and the app MUST be served from
    // PUBLIC_CLERK_DOMAIN — a domain registered as a satellite in the Clerk
    // dashboard. Absent/false → a normal primary instance.
    PUBLIC_CLERK_IS_SATELLITE: z
      .string()
      .optional()
      .transform((v) => v === "true"),
    PUBLIC_CLERK_DOMAIN: z.string().optional(),

    // Birdwatch Admin IDs
    PUBLIC_BIRDWATCH_TEAM_ADMIN_ID: z.string(),
    PUBLIC_BIRDWATCH_BOT_ADMIN_ID: z.string(),

    // Environment
    PUBLIC_ENV: z.enum(["development", "staging", "beta", "main"]),

    // Third-party Services
    PUBLIC_INVOICED_URL: z.url(),
    PUBLIC_SCHEDULE_LICENSE_KEY: z.string(),
    PUBLIC_GOOGLE_MAPS_API_KEY: z.string(),

    // Stripe
    PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    PUBLIC_STRIPE_DASHBOARD_URL: z
      .url()
      .default("https://dashboard.stripe.com"),

    // Sentry
    PUBLIC_SENTRY_ENABLED: z.coerce.number().transform((val) => val === 1),
    PUBLIC_SENTRY_DSN: z.url(),

    // Analytics & Monitoring
    PUBLIC_STATSIG_CLIENT_API_KEY: z.string(),
    PUBLIC_MIXPANEL_TOKEN: z.string().optional(),
    PUBLIC_GTM_ID: z.string().default("GTM-KKHS8XC"),
    PUBLIC_GTM_AUTH: z.string().default("aeObUcAnRCZZ-3TrdkDQjQ"),
    PUBLIC_GTM_PREVIEW: z.string().default("env-311"),

    // App URLs
    PUBLIC_WEBAPP_URL: z.url(),
    // Birdie chat API (birdie-harness) — the officeAgent HTTP chat surface
    PUBLIC_BIRDIE_URL: z.url().default("https://birdie.birdwatch.com"),
    // Birdie v4 server — the office agent for people inside the quote gate;
    // unset = everyone stays on the live bot (the kill switch, no default).
    // The admin Birdie monitor (`/birdie`) reads the same server.
    PUBLIC_BIRDIE_V4_URL: z.url().optional(),
    // Birdie v4 server for the member chat (/my/birdie, the customer agent,
    // behind rollout_customer_agent); unset turns the member chat off. Its own
    // switch, independent of PUBLIC_BIRDIE_V4_URL (the office chat's).
    PUBLIC_BIRDIE_MEMBER_CHAT_URL: z.url().optional(),
    PUBLIC_CATALOG_APP_URL: z.url().optional(),
    PUBLIC_LINK_APP: z.url().optional(),

    // @deprecated - Notion page is broken / being phased out
    PUBLIC_MEMBERSHIP_AGREEMENT_URL: z
      .url()
      .default("https://birdwatch.notion.site/membership-agreement"),

    // Owner app integrations
    PUBLIC_INTERCOM_APP_ID: z.string().optional(),
    PUBLIC_FILLOUT_FORM_ID: z.string().optional(),
    PUBLIC_TYPEFORM_FEEDBACK_ID: z.string().optional(),
    PUBLIC_HOMECARE_TAG_ID: z.string().optional(),

    // Media Service
    PUBLIC_MEDIA_SERVICE_URL: z
      .url()
      .optional()
      .default("http://localhost:4000"),
    // Realtime event relay (realtime.birdwatch.com) — SSE over Redis Streams.
    PUBLIC_REALTIME_URL: z
      .url()
      .optional()
      .default("https://realtime.birdwatch.com"),
    PUBLIC_SHORT_LINK_URL: z.string().optional(),
    PUBLIC_S3_MEDIA_URL: z.string().optional(),
    PUBLIC_SERVING_MEDIA_URL: z.string().optional(),
  },
  runtimeEnv: (import.meta as any).env,
  // Unit tests import modules that import this one (a pure calculation next to
  // a hook in the same barrel); they never read a value, so an unset variable
  // must not fail the suite. Vite sets MODE to "test" under vitest.
  skipValidation: (import.meta as any).env?.MODE === "test",
});
